// Mirrors the server-side platform detection in
// apps/middle-end/src/gql/types/Petition/mutations/ai/generateAiDraftFromSocialPost/platforms.
// Client and middle-end are separate apps so this can't be shared code; the server
// independently re-validates, so a drift here degrades gracefully (worst case a link is
// misrouted and the server returns its own error). TikTok-only for V1 — when a second platform
// is added, that's the point to introduce a registry here (the server-side one is the model).

export type SocialPostClassification =
	| { kind: 'freeform' }
	| { kind: 'tiktok_post'; url: string; handle: string | undefined }
	| { kind: 'unsupported_platform' }
	| { kind: 'invalid_link' };

function isTiktokHostname(hostname: string): boolean {
	const host = hostname.toLowerCase();
	return host === 'tiktok.com' || host.endsWith('.tiktok.com');
}

// For a TikTok URL, the post (with optional handle from the URL), or null when the URL isn't a
// resolvable post (a profile, a truncated path, an unknown shape).
function matchTiktokPost(url: URL): { handle: string | undefined } | null {
	const host = url.hostname.toLowerCase();
	// Full canonical post: /@handle/video/123 or /@handle/photo/123 — handle is extractable.
	const canonical = /^\/@([\w.-]+)\/(?:video|photo)\/\d+/.exec(url.pathname);
	if (canonical) return { handle: `@${canonical[1]}` };
	// Short share links carry no handle in the URL: vm./vt./m.tiktok.com/<id> or /t/<id>.
	const isShortHost = host !== 'tiktok.com' && host !== 'www.tiktok.com';
	if (isShortHost && url.pathname.length > 1) return { handle: undefined };
	if (/^\/t\/[\w-]+/.test(url.pathname)) return { handle: undefined };
	return null;
}

// URL-ish means a scheme, or a dotted host before any slash (so a bare word stays freeform).
function looksLikeUrl(input: string): boolean {
	return /^https?:\/\//i.test(input) || /^[^\s/]+\.[^\s/]+/.test(input);
}

function tryParseUrl(candidate: string): URL | undefined {
	try {
		const url = new URL(candidate);
		return url.protocol === 'http:' || url.protocol === 'https:' ? url : undefined;
	} catch {
		return undefined;
	}
}

// Parse as-is, then retry with an https prefix so scheme-less pastes (tiktok.com/@x) resolve.
function parseUrl(input: string): URL | undefined {
	return tryParseUrl(input) ?? tryParseUrl(`https://${input}`);
}

// A scheme-less token only counts as a URL when its host ends in a plausible TLD (two or more
// letters), so dotted prose like "U.S.A" or "e.g." stays freeform. An explicit scheme is
// always taken at face value.
function hasPlausibleTld(hostname: string): boolean {
	return /\.[a-z]{2,}$/i.test(hostname);
}

/**
 * Classifies raw hero input into a routing decision. A link is a single whitespace-free
 * token; anything with spaces is treated as freeform prose. TikTok posts are normalized to
 * https (so the value handed to the server always carries a scheme). Anything TikTok-shaped
 * but not a resolvable post is `invalid_link`; a URL on another platform is
 * `unsupported_platform`; everything else is `freeform`.
 */
export function classifySocialPostInput(input: string): SocialPostClassification {
	const trimmed = input.trim();
	if (trimmed.length === 0 || /\s/.test(trimmed) || !looksLikeUrl(trimmed)) {
		return { kind: 'freeform' };
	}

	const url = parseUrl(trimmed);
	if (!url) {
		return /tiktok\.com/i.test(trimmed) ? { kind: 'invalid_link' } : { kind: 'freeform' };
	}

	const hasScheme = /^https?:\/\//i.test(trimmed);
	if (!hasScheme && !hasPlausibleTld(url.hostname)) {
		return { kind: 'freeform' };
	}

	if (!isTiktokHostname(url.hostname)) return { kind: 'unsupported_platform' };

	const post = matchTiktokPost(url);
	if (!post) return { kind: 'invalid_link' };

	url.protocol = 'https:';
	return { kind: 'tiktok_post', url: url.href, handle: post.handle };
}
